/**
 * Phase-1 home (G09): what an audit buyer needs on one screen. The latest audit (status, score, the top 5 fixes, view
 * report, download), included re-runs left, invite a teammate, contact support. Audits paid for but not started yet are
 * offered first. Shown only to audit-only workspaces (AUDIT_ONLY_FLAG); everyone else gets the Quixo OS dashboard (HomeView).
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { ActionTile, Badge, Button, Card, CardHead, EmptyState, ErrorState, Loading, PageHeader, ScoreRing, Stat, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useFormat } from '../../../lib/format';
import { useResource, type ResourceState } from '../../../lib/resource';
import { Icon } from '../../../shell/Icon';
import { useWorkspaceName } from '../_shared/ListScreen';
import { canWrite, statusTone } from '../audit/AuditPage';
import { downloadArtifact } from '../audit/AuditDetailPage';
import { auditErrorKey, subjectLabel, textFor, tierKey, topFixes, type AuditJob, type AuditReport, type UnusedEntitlement } from '../audit/audit-view';
import { SUPPORT_EMAIL } from '../help/HelpPage';
import '../audit/audit.css';
import '../modules/modules.css';

/** Free re-runs left across original paid audits. */
export const rerunsLeft = (jobs: readonly AuditJob[]) => jobs.filter((j) => !j.rerunOf && j.includedReruns).reduce((n, j) => n + (j.includedReruns?.remaining ?? 0), 0);

export function AuditHome() {
  const { me } = useApp();
  const writer = canWrite(me?.roles);
  const jobs = useResource<AuditJob[]>(me?.tenantId ? '/v1/growth-audit/jobs' : null);
  const latest = jobs.status === 'ready' ? jobs.data[0] ?? null : null;
  const report = useResource<AuditReport>(latest?.status === 'ready' ? `/v1/growth-audit/jobs/${encodeURIComponent(latest.id)}/report?format=json` : null);
  const unused = useResource<UnusedEntitlement[]>(writer && me?.tenantId ? '/v1/growth-audit/entitlements' : null);
  return <AuditHomeView jobs={jobs} report={latest?.status === 'ready' ? report : null} unused={writer ? unused : null} />;
}

export function AuditHomeView({ jobs, report, unused }: { jobs: ResourceState<AuditJob[]>; report: ResourceState<AuditReport> | null; unused: ResourceState<UnusedEntitlement[]> | null }) {
  const { t, me, locale } = useApp();
  const f = useFormat();
  const workspace = useWorkspaceName();
  const [err, setErr] = useState<string | null>(null);
  const loc: 'en' | 'ar' = locale === 'ar' ? 'ar' : 'en';
  const latest = jobs.status === 'ready' ? jobs.data[0] ?? null : null;
  const analysis = report?.status === 'ready' ? report.data.analysis : null;
  const pending = unused?.status === 'ready' ? unused.data : [];
  const owner = me?.roles?.some((r) => r === 'customer_owner' || r === 'customer_admin');

  return (
    <section className="qm-page qm-audit" aria-labelledby="page-title">
      <PageHeader eyebrow={workspace} title={t('shell.p1.home.title')} intro={t('shell.p1.home.intro')} />

      {pending.length ? (
        <Card aria-labelledby="home-start" className="qm-audit__banner">
          <CardHead id="home-start" title={t('shell.p1.start.title')} sub={t('shell.p1.start.sub')} />
          <Link className="qm-button" data-variant="primary" to="/audit">{t('shell.p1.start.cta')}</Link>
        </Card>
      ) : null}

      <Card aria-labelledby="home-latest">
        <CardHead id="home-latest" title={t('shell.p1.home.latest')} trailing={latest ? <Badge tone={statusTone(latest.status)}>{t(`shell.screens.auditDash.status.${latest.status}`)}</Badge> : undefined} />
        {jobs.status === 'loading' ? <Loading label={t('common.loading')} /> : jobs.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : !latest ? (
          <EmptyState title={t('shell.p1.home.none')}>
            <Text tone="muted">{t('shell.p1.home.noneHint')}</Text>
            <Link className="qm-button" data-variant="primary" to="/audit">{t('shell.screens.audit.buy')}</Link>
          </EmptyState>
        ) : (
          <>
            <Text><bdi>{f.text(subjectLabel(latest.targetUrl, analysis).label)}</bdi>{tierKey(latest.tier) ? ` · ${t(`shell.p1.tier.${tierKey(latest.tier)}`)}` : ''} · {f.date(latest.createdAt)}</Text>
            {latest.status !== 'ready' ? <Text tone="muted">{t(latest.status === 'delayed' ? 'shell.screens.auditDash.delayedHelp' : 'shell.p1.home.running')}</Text> : null}
            {analysis ? (
              <div className="qm-p1home__score">
                {analysis.overall !== null ? <ScoreRing value={analysis.overall} label={t('shell.p1.report.overall')} /> : null}
                <div>
                  <b>{t('shell.p1.home.topFixes')}</b>
                  <ol className="qm-p1home__fixes">
                    {topFixes(analysis, 5).map((x) => { const tx = textFor(x.title, loc)!; return <li key={x.id} lang={tx.lang !== loc ? tx.lang : undefined}>{tx.text}</li>; })}
                  </ol>
                </div>
              </div>
            ) : null}
            <div className="qm-row">
              <Link className="qm-button" data-variant="primary" to={`/audit/${encodeURIComponent(latest.id)}`}>{t('shell.p1.home.viewReport')}</Link>
              {latest.status === 'ready' && latest.artifacts.some((a) => a.kind === 'pdf') ? (
                <Button onClick={() => { setErr(null); downloadArtifact(latest.id, 'pdf').catch((e) => setErr(t(auditErrorKey(e)))); }}>{t('shell.p1.detail.downloadPdf')}</Button>
              ) : null}
            </div>
            {err ? <Text role="alert">{err}</Text> : null}
          </>
        )}
      </Card>

      <div className="qm-audit__grid">
        <Card>
          <Stat label={t('shell.screens.auditDash.rerunsLeft')} value={jobs.status === 'ready' ? f.number(rerunsLeft(jobs.data)) : '-'} />
          <Text tone="muted">{t('shell.screens.auditDash.rerunsHint')}</Text>
        </Card>
        <Card aria-labelledby="home-next">
          <CardHead id="home-next" title={t('shell.p1.home.next')} />
          <div className="qm-module__links">
            {owner ? <ActionTile href="/team" icon={<Icon name="user-plus" size={18} />} title={t('shell.p1.home.invite')} desc={t('shell.p1.home.inviteDesc')} /> : null}
            <ActionTile href="/help" icon={<Icon name="headset" size={18} />} title={t('shell.p1.home.support')} desc={SUPPORT_EMAIL} />
            <ActionTile href="/audit/dashboard" icon={<Icon name="audit" size={18} />} title={t('shell.screens.auditDash.title')} />
          </div>
        </Card>
      </div>
    </section>
  );
}
